import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SuperAdminIcon from './SuperAdminIcon';
import { superAdminDestinations } from './superAdminNavigation';
import { useAuth } from '../../../context/AuthContext';
import { fetchGlobalAdminSearchRecords } from '../services/superAdminSearchService';

const RECENTS_KEY = 'crickarena:super-admin:search-recents';
const FAVORITES_KEY = 'crickarena:super-admin:search-favorites';
const readStored = (key) => { try { return JSON.parse(window.localStorage.getItem(key) || '[]'); } catch { return []; } };
const staticResults = [
  ...superAdminDestinations.map((item) => ({ ...item, key: `route:${item.to}`, description: item.group })),
  { key: 'setting:registrations', label: 'User registrations', description: 'Platform setting', group: 'Settings', icon: 'settings', to: '/sa/settings' },
];
const fuzzyScore = (result, rawQuery) => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return 1;
  const text = `${result.label} ${result.description || ''} ${result.searchText || ''} ${result.group} ${result.key}`.toLowerCase();
  if (text.includes(query)) return 100 - text.indexOf(query);
  let cursor = 0; let score = 0;
  for (const character of query) { const next = text.indexOf(character, cursor); if (next < 0) return 0; score += Math.max(1, 12 - (next - cursor)); cursor = next + 1; }
  return score;
};

const SuperAdminCommandPalette = ({ open, onClose, restoreFocusRef }) => {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState(() => readStored(RECENTS_KEY));
  const [favorites, setFavorites] = useState(() => readStored(FAVORITES_KEY));
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { token } = useAuth();
  const results = useMemo(() => {
    const all = [...staticResults, ...resources].filter((item, index, list) => list.findIndex((candidate) => candidate.key === item.key) === index);
    if (!query.trim()) {
      const byKey = new Map(all.map((item) => [item.key, item]));
      const favored = favorites.map((key) => byKey.get(key)).filter(Boolean).map((item) => ({ ...item, displayGroup: 'Favorites' }));
      const recent = recents.map((key) => byKey.get(key)).filter(Boolean).filter((item) => !favorites.includes(item.key)).map((item) => ({ ...item, displayGroup: 'Recent' }));
      return [...favored, ...recent, ...staticResults.slice(0, 8).filter((item) => !favorites.includes(item.key) && !recents.includes(item.key)).map((item) => ({ ...item, displayGroup: 'Routes' }))];
    }
    return all.map((item) => ({ ...item, score: fuzzyScore(item, query), displayGroup: item.group })).filter((item) => item.score > 0).sort((a, b) => b.score - a.score).slice(0, 40);
  }, [favorites, query, recents, resources]);

  const groupedResults = useMemo(() => results.reduce((groups, item, index) => {
    const name = item.displayGroup || item.group;
    if (!groups[name]) groups[name] = [];
    groups[name].push({ ...item, resultIndex: index });
    return groups;
  }, {}), [results]);

  const selectDestination = useCallback((item) => {
    const nextRecents = [item.key, ...recents.filter((key) => key !== item.key)].slice(0, 8);
    setRecents(nextRecents);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(nextRecents));
    navigate(item.to);
    onClose();
  }, [navigate, onClose, recents]);

  const toggleFavorite = (item) => {
    const next = favorites.includes(item.key) ? favorites.filter((key) => key !== item.key) : [...favorites, item.key];
    setFavorites(next);
    window.localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
  };

  useEffect(() => { setActiveIndex(0); }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    inputRef.current?.focus();
    const restoreTarget = restoreFocusRef?.current;
    return () => restoreTarget?.focus();
  }, [open, restoreFocusRef]);

  useEffect(() => {
    if (!open || !token || resources.length) return undefined;
    let current = true;
    setLoading(true);
    fetchGlobalAdminSearchRecords(token).then((items) => { if (current) setResources(items); }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [open, resources.length, token]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((current) => results.length ? (current + 1) % results.length : 0);
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((current) => results.length ? (current - 1 + results.length) % results.length : 0);
      }
      if (event.key === 'Enter' && results[activeIndex]) {
        event.preventDefault();
        selectDestination(results[activeIndex]);
      }
      if (event.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll('button:not([disabled]), input:not([disabled])');
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, onClose, open, results, selectDestination]);

  if (!open) return null;

  return (
    <div className="sa-command-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} className="sa-command" role="dialog" aria-modal="true" aria-labelledby="sa-command-title">
        <h2 className="visually-hidden" id="sa-command-title">Search Super Admin</h2>
        <div className="sa-command__search">
          <SuperAdminIcon name="search" />
          <input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search routes, users, products, organizations…" aria-label="Search Super Admin" aria-activedescendant={results[activeIndex] ? `sa-command-result-${activeIndex}` : undefined} aria-controls="sa-command-results" />
          <button className="sa-command__escape" type="button" onClick={onClose} aria-label="Close search">Esc</button>
        </div>
        <div className="sa-command__results" id="sa-command-results" role="listbox" aria-label="Super Admin destinations">
          {loading ? <p className="sa-command__loading">Loading authorized resources…</p> : null}
          {results.length ? Object.entries(groupedResults).map(([group, items]) => <section className="sa-command__group" key={group}><h3>{group}</h3>{items.map((item) => <div className={activeIndex === item.resultIndex ? 'is-active' : ''} key={item.key}><button id={`sa-command-result-${item.resultIndex}`} role="option" aria-selected={activeIndex === item.resultIndex} type="button" onMouseMove={() => setActiveIndex(item.resultIndex)} onClick={() => selectDestination(item)}><SuperAdminIcon name={item.icon} /><span><strong>{item.label}</strong><small>{item.description || item.group}</small></span></button><button className="sa-command__favorite" type="button" aria-label={`${favorites.includes(item.key) ? 'Remove' : 'Add'} ${item.label} ${favorites.includes(item.key) ? 'from' : 'to'} favorites`} onClick={() => toggleFavorite(item)}>{favorites.includes(item.key) ? '★' : '☆'}</button></div>)}</section>) : <p>No results match “{query}”.</p>}
        </div>
      </section>
    </div>
  );
};

export default SuperAdminCommandPalette;
